import { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAsync } from 'react-use';
import { Checkbox, CircularProgress } from '@getgo/chameleon-web-react-wrapper';

import { useAppDispatch, useAppSelector } from 'hooks';
import useProvisioning from 'hooks/useProvisioning';
import { AdyenWrapper, loadAdyenACHCheckout } from 'lib/adyen-client';
import { showErrorSnack } from 'modules/error';
import { accountKey, locale } from 'modules/global-wrapper';
import { isPaymentMethodPresent } from 'modules/payment-methods';
import { isNewTabPayment, sessionInvoiceNumber, sessionSuccessRedirectUrl } from 'modules/session-details';
import Track, { OnePayEventNames, PaymentMethodEventNames } from 'modules/tracking';
import { postTransactionAdyenFinalize, postTransactionAdyenPrepare } from 'modules/transaction-adyen';
import { FinalizeAdyenResponse, PrepareAdyenResp } from 'types/transaction-adyen';
import {
  ACH_ADD,
  ACH_ADD_PAY,
  COPAS_PAYMENT_STATUS,
  PAYMENT_METHODS_TYPES,
  SEARCH_PARAMS,
  THEMES,
  TRACKING_EVENT_STATUS,
} from 'utils/constants';
import st from 'utils/shared-translations';

import './adyen-ach-form.css';

interface AdyenACHFormProps {
  intent: string;
}

let AdyenClient: AdyenWrapper | undefined = undefined;
let ach: any = undefined;

const loadExternalScripts = async (prepareResp: PrepareAdyenResp) => {
  const { adyenJsSettings } = prepareResp;
  AdyenClient = await loadAdyenACHCheckout(adyenJsSettings);
};

const AdyenACHForm: FC<AdyenACHFormProps> = ({ intent }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [isLoading, setLoading] = useState(true);
  const isDefault = useRef(false);

  const { processQuote, processRenewal, processInvoice } = useProvisioning();

  const selectedGlobalAccountKey = useAppSelector(accountKey);
  const selectedGlobalLocale = useAppSelector(locale);

  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';
  const globalTheme = localStorage.getItem(SEARCH_PARAMS.theme);

  // Session details selectors
  const selectedSuccessRedirectUrl = useAppSelector(sessionSuccessRedirectUrl);
  const selectedIsNewTabPayment = useAppSelector(isNewTabPayment);

  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const selectedPaymentMethodIsPresent = useAppSelector(isPaymentMethodPresent);

  const onPaymentCompleted = async (result, component, prepareResp) => {
    try {
      const response: FinalizeAdyenResponse = await dispatch(
        postTransactionAdyenFinalize({
          checkoutSessionId: prepareResp.adyenSessionId,
          checkoutSessionResult: result.sessionResult,
          setAsDefault: isDefault.current,
          paymentMethod: 'ach',
        }),
      ).unwrap();
      Track(OnePayEventNames.adyenACHPaymentCompleted, { status: response.status });
      if (response.status !== COPAS_PAYMENT_STATUS.SUCCESS) {
        throw new Error('Adyen finalization failed');
      } else if (intent === ACH_ADD_PAY) {
        if (selectedIsNewTabPayment) window.close();
        else if (renewalDate) processRenewal(PAYMENT_METHODS_TYPES.achAdyen);
        else if (selectedInvoiceNumber) processInvoice(PAYMENT_METHODS_TYPES.achAdyen);
        else processQuote(PAYMENT_METHODS_TYPES.achAdyen);
      } else {
        window.location.assign(selectedSuccessRedirectUrl);
      }
    } catch {
      Track(PaymentMethodEventNames.achSaveCTA, { status: TRACKING_EVENT_STATUS.copasFailure });
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  const onError = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenACHFormError, { error });
  };

  const onPaymentFailed = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenACHPaymentFailed, { error });
  };

  const getTranslations = () => {
    if (intent === ACH_ADD) {
      return {
        [selectedGlobalLocale]: {
          confirmPurchase: intl.formatMessage(st['ach.form.cta.add']),
        },
      };
    } else {
      return {};
    }
  };

  const initializeAdyenCard = async (prepareResp: PrepareAdyenResp) => {
    const configuration = {
      environment: prepareResp.adyenEnvironment,
      clientKey: prepareResp.adyenClientKey,
      session: {
        id: prepareResp.adyenSessionId,
        sessionData: prepareResp.adyenSessionData,
      },
      translations: getTranslations(),
      onPaymentCompleted: (result, component) => onPaymentCompleted(result, component, prepareResp),
      onError,
      onPaymentFailed,
    };
    try {
      if (AdyenClient) {
        ach = await AdyenClient.initialize(configuration);
        setLoading(false);
        ach.mount('#adyen-ach-form');
      }
    } catch {
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  useAsync(async () => {
    if (selectedGlobalAccountKey) {
      try {
        const resp: PrepareAdyenResp = await dispatch(
          postTransactionAdyenPrepare({
            paymentMethodKey: null,
            browserLocale: selectedGlobalLocale,
            returnUrl: window.location.origin,
            paymentMethod: 'ach',
          }),
        ).unwrap();
        await loadExternalScripts(resp);
        initializeAdyenCard(resp);
      } catch {
        Track(PaymentMethodEventNames.prepareACHResponse, { status: TRACKING_EVENT_STATUS.copasFailure });
        dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
      }
    }
  }, [selectedGlobalAccountKey]);

  return (
    <>
      <div
        id="adyen-ach-form"
        className={globalTheme?.toUpperCase() === THEMES.dark ? 'adyen-ach-dark-mode' : ''}
      ></div>
      {isLoading && (
        <div className="adyen-ach-form__loader">
          <CircularProgress />
        </div>
      )}
      {!isLoading && selectedPaymentMethodIsPresent && (
        <section className="adyen-ach-form__set-default">
          <Checkbox onChange={(e) => (isDefault.current = e.target.checked)}>
            {intl.formatMessage(st['payment.method.set.as.default'])}
          </Checkbox>
        </section>
      )}
    </>
  );
};

export default AdyenACHForm;
